import React from 'react';
import { Button, Icon, Link } from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import * as QUERIES from '../../api/document/files/queries-services/queries';
import {
  RetrieveS3URLResponse,
  S3FileIOPayLoadItems,
} from '../../api/document/files/api-services/model';
import { ServiceError } from '../../hooks/error';
import { FileUploadState } from '../../models/File';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  downloadButton: {
     ...typeTheme(theme, 'buttonSmall'),
    color: theme.color.link.linkDefault01,
  },
  customDownloadButton: {
     ...typeTheme(theme, 'buttonSmall'),
    color: theme.color.icon.iconInverse,
  },
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  tableRowDownload: {
    display: 'flex',
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
    color: theme.color.link.linkActive01
  },
}));

export interface DownloadFromS3ActionProps {
  actionType: 'btn' | 'link' | 'contained';
  id?: string;
  label: string;
  requestRoute: S3FileIOPayLoadItems;
  onClick: (
    event: React.MouseEvent<
      HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement,
      MouseEvent
    > | null
  ) => void;
  onDownload: (status: RetrieveS3URLResponse) => void;
  onError?: (error: ServiceError) => void;
  isLoading?: boolean;
  disable?: boolean;
  color?: 'primary' | 'secondary';
  isDisplayDownloadLabelWithIcon?: boolean;
  isTableRowDownload?: boolean;
  isStartIcon?: boolean;
  isCustomColor?: boolean;
}

const DownloadFromS3Action: React.FC<DownloadFromS3ActionProps> = (
  props: DownloadFromS3ActionProps
) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const {
    actionType = 'btn',
    label = 'DOWNLOAD FILE',
    id,
    requestRoute,
    onClick,
    onDownload = (status) => null,
    onError = (status) => null,
    isLoading = false,
    disable = false,
    color = 'secondary',
    isDisplayDownloadLabelWithIcon = false,
    isTableRowDownload = false,
    isStartIcon,
    isCustomColor,
  } = props;

  /** fetch pre-sign url from api and download from S3
   * */
  QUERIES.useGetRetrieveS3PreSignUrl(requestRoute, {
    cacheTime: 0,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: requestRoute.data !== '',
    refetchInterval: 5000,
    onSuccess: (response) => {
      if (response.fileStatus === FileUploadState.SUCCESS && requestRoute.data !== '') {
        const newWindow = window.open(response.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
      }
      onDownload(response);
    },
    onError: (error) => {
      onError(error);
    },
  });

  if (isLoading) {
    return <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />;
  }

  const renderActionType = () => {
    switch (actionType) {
      case 'btn':
        return 'outlined';
      case 'link':
        return 'text';
      case 'contained':
        return 'contained';
      default:
        return 'text';
    }
  };

  const displayDownloadLink = () => {
    if (isDisplayDownloadLabelWithIcon) {
      return (
        <Button className={classes.downloadPdf} onClick={onClick} variant="text">
          <Icon name={IconName.download} size="M" color={theme.color.link.linkDefault01} />
          <span className={classes.downloadPdfText}>{label}</span>
        </Button>
      );
    }
    if (isTableRowDownload) {
      return (
        <Button
          className={classes.tableRowDownload}
          onClick={onClick}
          variant="text"
        >
          <Icon name={IconName.download} size="M" color={theme.color.link.linkDefault01} />
          <span className={classes.downloadPdfText}>{label}</span>
        </Button>
      );
    }

    return (
      <Button
        id={id}
        className={isCustomColor ? classes.customDownloadButton : classes.downloadButton}
        variant={renderActionType()}
        onClick={onClick}
        disabled={disable}
        color={color}
        startIcon={
          isStartIcon ? (
            <Icon name={IconName.export} size="S" color={theme.color.icon.iconInverse} />
          ) : null
        }
      >
        {label}
      </Button>
    );
  };

  return <>{displayDownloadLink()}</>;
};
export default DownloadFromS3Action;
